import * as Request from "./xhr";
import { getDrinks, XHR_STATE } from "./xhr";
import { Dispatch } from "@reduxjs/toolkit";
import {
  setModalState,
  updateFeedback,
  updateMediaXhr,
  updateXHR,
} from "../redux/slices/AppState";
import { MODAL } from "../components/modal/helpers/constants";
import {
  clearMediaSelection,
  setCategory,
  setMediaPhotos,
  setProductAddType,
  spreadProductUpdate,
} from "../redux/slices/ProductState";
import {
  BusinessInfo,
  CampaignSettings,
  Currency,
  FEEDBACK_ALERTS,
  Language,
  NewComboPayload,
  NewPackage,
  Package,
  PasswordUpdateData,
  ProductInterface,
  Segment,
  UpdateComboPayload,
  UpdatePackagePayload,
  User,
  XHR_Error,
} from "../types/common.types";
import { FEEDBACK_VARIANT } from "../components/extras/Alert";
import { NavigateFunction } from "react-router-dom";
import { ProductType } from "../components/product";
import { get, set } from "lockr";
import { setUserState, USER_STATE } from "../redux/slices/Auth";
import { AxiosResponse } from "axios";
import {
  addPackage,
  removePackage,
  setPackages,
  updateActivePackage,
  updatePackage,
} from "../redux/slices/PackageState";
import {
  setSettings,
  updateMaxDailyWins,
} from "../redux/slices/CampaignSettingsState";
import {
  setCombos,
  addCombo,
  updateCombo,
  removeCombo,
} from "../redux/slices/ComboState";

interface ProductWrite {
  data: any; //ProductFormData;
  dispatch: Dispatch<any>;
}

export const getStaffPermission = () => {
  const staffPermissions = get("staff-permissions");
  return staffPermissions;
};

const hasStaffPermission = (
  staffPermissions: string[] | undefined,
  permission: string
) => {
  if (!staffPermissions) return true;
  const aliases: Record<string, string[]> = {
    settings_modification: [
      "business_settings_write",
      "menu_settings_write",
      "business_settings_view",
      "menu_settings_view",
    ],
  };
  const expanded = new Set(
    staffPermissions.flatMap((item) => aliases[item] || item)
  );
  return expanded.has(permission);
};

export const AddCategory = async (
  dispatch: Dispatch<any>,
  categoryName: string,
  productType: ProductType,
  usingProductState: boolean = false
) => {
  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));
  try {
    await Request.addProductCategory(categoryName, productType);
    if (usingProductState) {
      dispatch(setCategory(""));
    }
    if (productType === ProductType.Drink) {
      Request.getDrinkCategories(dispatch);
    } else {
      Request.getFoodCategories(dispatch);
    }

    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.SUCCESS,
        message: `${categoryName} is added successfully!`,
        timeout: 5000,
      })
    );
  } catch (error) {
    console.error(error);
    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.ERROR,
        message: `Failed to add ${categoryName}. Please try again.`,
        timeout: 10000,
      })
    );
  } finally {
    dispatch(updateXHR({ xhr: XHR_STATE.NONE }));
  }
};

export const UpdateProductCategory = async (
  dispatch: Dispatch<any>,
  categoryId: string,
  categoryName: string,
  productType: ProductType.Drink | ProductType.Food
) => {
  const staffPermissions = getStaffPermission();
  if (staffPermissions && !staffPermissions.includes("product_write")) {
    throw new Error("Access denied!");
  }
  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));
  try {
    const response = await Request.updateProductCategory(
      categoryId,
      categoryName,
      productType
    );

    if (response.data.success) {
      if (productType === ProductType.Drink) {
        Request.getDrinks("Loader", dispatch);
      } else {
        Request.getFoodProducts("Loader", dispatch);
      }
    }

    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.SUCCESS,
        message: `Category updated successfully!`,
        timeout: 5000,
      })
    );
    return response.data.success;
  } catch (error) {
    console.error("ERROR UPDATING PRODUCT CATEGORY", error);
    dispatch(updateXHR({ xhr: XHR_STATE.FAILED }));
    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.ERROR,
        message: `Failed to update category, please try again.`,
        timeout: 10000,
      })
    );
    return false;
  } finally {
    dispatch(updateXHR({ xhr: XHR_STATE.NONE }));
  }
};

export const DeleteProductCategory = async (
  dispatch: Dispatch<any>,
  categoryId: string,
  productType: ProductType.Drink | ProductType.Food
) => {
  const staffPermissions = getStaffPermission();
  if (staffPermissions && !staffPermissions.includes("product_write")) {
    throw new Error("Access denied!");
  }
  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));
  try {
    const response = await Request.deleteProductCategory(
      categoryId,
      productType
    );

    if (response.data.success) {
      if (productType === ProductType.Drink) {
        Request.getDrinks("Loader", dispatch);
      } else {
        Request.getFoodProducts("Loader", dispatch);
      }
    }
    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.SUCCESS,
        message: `Category deleted successfully!`,
        timeout: 5000,
      })
    );
    return response.data.success;
  } catch (error) {
    dispatch(updateXHR({ xhr: XHR_STATE.FAILED }));
    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.ERROR,
        message: `Failed to delete category, please try again.`,
        timeout: 10000,
      })
    );
  } finally {
    dispatch(updateXHR({ xhr: XHR_STATE.NONE }));
  }
};

export const CreateDrink = async (props: ProductWrite): Promise<boolean> => {
  const staffPermissions = getStaffPermission();
  if (staffPermissions && !staffPermissions.includes("product_write")) {
    throw new Error("Access denied!");
  }
  props.dispatch(updateXHR({ xhr: Request.XHR_STATE.REQUESTING }));
  try {
    const response = await Request.InsertDrink(props.data);
    const { data } = response;

    if (data.success) {
      props.dispatch(setProductAddType(null));
      props.dispatch(updateXHR({ xhr: Request.XHR_STATE.SUCCESS }));
      props.dispatch(setModalState(MODAL.VIEW));

      await getDrinks("Helper", props.dispatch);
      props.dispatch(
        updateFeedback({
          show: true,
          type: FEEDBACK_VARIANT.ERROR,
          message: "Product created successfully!",
          timeout: 5000,
        })
      );
      return true;
    } else {
      throw new Error("Failed to create drink.");
    }
  } catch (error: any) {
    console.error("CreateDrink Error:", error);
    props.dispatch(updateXHR({ xhr: Request.XHR_STATE.FAILED }));
    props.dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.ERROR,
        message: error.message || "An error occurred while creating the drink.",
        timeout: 10000,
      })
    );
    return false;
  }
};

export const UpdateDrink = async (props: ProductWrite, productId: string) => {
  const staffPermissions = getStaffPermission();
  if (staffPermissions && !staffPermissions.includes("product_write")) {
    throw new Error("Access denied!");
  }
  props.dispatch(updateXHR({ xhr: Request.XHR_STATE.REQUESTING }));

  try {
    const response = await Request.UpdateDrink(props.data, productId);
    const { data } = response;
    if (data.success) {
      props.dispatch(
        updateFeedback({
          show: true,
          type: FEEDBACK_VARIANT.SUCCESS,
          message: FEEDBACK_ALERTS.PRODUCT_UPDATED,
        })
      );
      props.dispatch(setProductAddType(null));
      props.dispatch(
        spreadProductUpdate({
          product: data.updatedProduct,
          productType: ProductType.Drink,
        })
      );
      props.dispatch(updateXHR({ xhr: Request.XHR_STATE.SUCCESS }));
      props.dispatch(setModalState(MODAL.VIEW));
    }
    return data.success;
  } catch (error: any) {
    const errorData = error.response?.data as XHR_Error;
    const message =
      errorData?.error?.message ||
      "An error occurred while updating the drink.";
    props.dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.ERROR,
        message,
        timeout: 10000,
      })
    );
    return false;
  }
};

export const DeleteDrink = (
  drink: ProductInterface,
  dispatch: Dispatch<any>,
  navigate: NavigateFunction
) => {
  const staffPermissions = getStaffPermission();
  if (staffPermissions && !staffPermissions.includes("product_write")) {
    throw new Error("Access denied!");
  }
  dispatch(updateXHR({ xhr: Request.XHR_STATE.REQUESTING }));
  return Request.deleteDrink(drink)
    .then((response) => {
      Request.getDrinks("Helper", dispatch);
      dispatch(
        updateFeedback({
          show: true,
          type: FEEDBACK_VARIANT.SUCCESS,
          message: response.data.message,
        })
      );
      dispatch(updateXHR({ xhr: Request.XHR_STATE.SUCCESS }));
      setTimeout(() => navigate("/app/drinks"), 1500);
    })
    .catch((error) => {
      const errorData = error.response?.data;
      const message =
        errorData && errorData.error.message
          ? errorData.error.message
          : "An error occurred while deleting the drink.";

      dispatch(
        updateFeedback({
          show: true,
          type: FEEDBACK_VARIANT.ERROR,
          message,
          timeout: 10000,
        })
      );

      dispatch(updateXHR({ xhr: Request.XHR_STATE.FAILED }));
      return false;
    });
};

//=========== Food ======

export const CreateFood = (props: ProductWrite) => {
  const staffPermissions = getStaffPermission();
  if (staffPermissions && !staffPermissions.includes("product_write")) {
    throw new Error("Access denied!");
  }
  props.dispatch(updateXHR({ xhr: Request.XHR_STATE.REQUESTING }));
  return Request.InsertFood(props.data)
    .then((response) => {
      const { data } = response;
      if (data.success) {
        props.dispatch(
          updateFeedback({
            show: false,
            type: FEEDBACK_VARIANT.SUCCESS,
            message: FEEDBACK_ALERTS.PRODUCT_ADDED,
          })
        );
        props.dispatch(setProductAddType(null));
        props.dispatch(updateXHR({ xhr: Request.XHR_STATE.SUCCESS }));
        props.dispatch(setModalState(MODAL.VIEW));
      }

      return data.success;
    })
    .catch((error) => {
      console.error(error);
      props.dispatch(updateXHR({ xhr: Request.XHR_STATE.FAILED }));
      props.dispatch(
        updateFeedback({
          show: false,
          type: FEEDBACK_VARIANT.ERROR,
          message: ` ${error.message || "Failed to create product"}.`,
        })
      );
    });
};

export const UpdateFood = async (props: ProductWrite, productId: string) => {
  const staffPermissions = getStaffPermission();
  if (staffPermissions && !staffPermissions.includes("product_write")) {
    throw new Error("Access denied!");
  }
  props.dispatch(updateXHR({ xhr: Request.XHR_STATE.REQUESTING }));

  try {
    const response = await Request.UpdateFood(props.data, productId);
    const { data } = response;
    if (data.success) {
      props.dispatch(
        updateFeedback({
          show: true,
          type: FEEDBACK_VARIANT.SUCCESS,
          message: FEEDBACK_ALERTS.PRODUCT_UPDATED,
        })
      );
      props.dispatch(setProductAddType(null));
      props.dispatch(
        spreadProductUpdate({
          product: data.updatedProduct,
          productType: ProductType.Food,
        })
      );
      props.dispatch(updateXHR({ xhr: Request.XHR_STATE.SUCCESS }));
      props.dispatch(setModalState(MODAL.VIEW));
    }
    return data.success;
  } catch (error: any) {
    const errorData = error.response?.data as XHR_Error;
    const message =
      errorData?.error?.message ||
      "An error occurred while updating the product.";
    props.dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.ERROR,
        message,
        timeout: 10000,
      })
    );
    return false;
  }
};

export const DeleteFood = (
  drink: ProductInterface,
  dispatch: Dispatch<any>,
  navigate: NavigateFunction
) => {
  const staffPermissions = getStaffPermission();
  if (staffPermissions && !staffPermissions.includes("product_write")) {
    throw new Error("Access denied!");
  }
  dispatch(updateXHR({ xhr: Request.XHR_STATE.REQUESTING }));
  return Request.deleteFood(drink)
    .then((response) => {
      Request.getFoodProducts("Helper", dispatch);
      dispatch(
        updateFeedback({
          show: true,
          type: FEEDBACK_VARIANT.SUCCESS,
          message: response.data.message,
        })
      );
      dispatch(updateXHR({ xhr: Request.XHR_STATE.SUCCESS }));
      setTimeout(() => navigate("/app/food"), 1500);
    })
    .catch((error) => {
      const errorData = error.response?.data;
      const message =
        errorData && errorData.error.message
          ? errorData.error.message
          : "An error occurred while deleting the food.";

      dispatch(
        updateFeedback({
          show: true,
          type: FEEDBACK_VARIANT.ERROR,
          message,
          timeout: 10000,
        })
      );

      dispatch(updateXHR({ xhr: Request.XHR_STATE.FAILED }));
      return false;
    });
};

//=========== Settings ===========

export const updateBusinessInfo = async (
  dispatch: Dispatch<any>,
  updateData: BusinessInfo
) => {
  const staffPermissions = getStaffPermission();
  if (!hasStaffPermission(staffPermissions, "business_settings_write")) {
    throw new Error("Access denied!");
  }
  dispatch(updateXHR({ xhr: Request.XHR_STATE.REQUESTING }));
  const response = await Request.updateBusinessInfo(updateData);
  if (response.data.success) {
    const user: User = get("user");
    const updatedUser = response.data.data || {};
    user.address = updatedUser.address ?? updateData.address ?? user.address;
    user.brief = updatedUser.brief ?? updateData.brief ?? user.brief;
    user.contacts = updatedUser.contacts ?? updateData.contacts ?? user.contacts;
    user.activities =
      updatedUser.activities ?? updateData.activities ?? user.activities;
    user.businessProfile =
      updatedUser.businessProfile ??
      updateData.businessProfile ??
      user.businessProfile;
    set("user", user);
    dispatch(setUserState({ state: USER_STATE.LOGGED_IN, user }));
  }
};

export const AddLanguage = async (
  dispatch: Dispatch<any>,
  languages: Language[]
) => {
  const staffPermissions = getStaffPermission();
  if (!hasStaffPermission(staffPermissions, "business_settings_write")) {
    throw new Error("Access denied!");
  }

  dispatch(updateXHR({ xhr: Request.XHR_STATE.REQUESTING }));
  const response = await Request.addLanguage(languages);
  if (response.data.success) {
    const user: User = get("user");
    user.languages = languages;
    set("user", user);
    dispatch(setUserState({ state: USER_STATE.LOGGED_IN, user }));
  }
  return true;
};

export const updateDefaultLanguage = (
  dispatch: Dispatch<any>,
  languageISO: string
) => {
  const staffPermissions = getStaffPermission();
  if (!hasStaffPermission(staffPermissions, "business_settings_write")) {
    throw new Error("Access denied!");
  }
  Request.updateDefaultLanguage(languageISO).then((response) => {
    if (response.data.success) {
      const user: User = get("user");
      user.defaultLanguage = languageISO;
      set("user", user);
      dispatch(setUserState({ state: USER_STATE.LOGGED_IN, user }));
    }
  });
};

export const changeCurrency = async (
  dispatch: Dispatch<any>,
  currency: Currency
): Promise<AxiosResponse<any>> => {
  const staffPermissions = getStaffPermission();
  if (!hasStaffPermission(staffPermissions, "business_settings_write")) {
    throw new Error("Access denied!");
  }

  try {
    const response = await Request.updateCurrency(currency);
    if (response.data.success) {
      const user: User = get("user");
      user.currency = currency;
      set("user", user);
      dispatch(setUserState({ state: USER_STATE.LOGGED_IN, user }));
    }
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const updateBanner = (dispatch: Dispatch<any>, bannerId: string) => {
  const staffPermissions = getStaffPermission();
  if (!hasStaffPermission(staffPermissions, "menu_settings_write")) {
    throw new Error("Access denied!");
  }
  dispatch(updateXHR({ xhr: Request.XHR_STATE.REQUESTING }));
  Request.updateBanner(bannerId).then((response) => {
    if (response.data.success) {
      const user: User = get("user");
      user.banner = response.data.data.banner;
      set("user", user);
      dispatch(setUserState({ state: USER_STATE.LOGGED_IN, user }));
      dispatch(updateXHR({ xhr: Request.XHR_STATE.SUCCESS }));
    }
  });
};

export const addBusinessLogo = (dispatch: Dispatch<any>, logo: string) => {
  const staffPermissions = getStaffPermission();
  if (!hasStaffPermission(staffPermissions, "menu_settings_write")) {
    throw new Error("Access denied!");
  }
  dispatch(updateXHR({ xhr: Request.XHR_STATE.REQUESTING }));
  Request.addBusinessLogo(logo).then((response) => {
    if (response.data.success) {
      const user: User = get("user");
      user.logo = response.data.data.logo;
      set("user", user);
      dispatch(clearMediaSelection());
      dispatch(setUserState({ state: USER_STATE.LOGGED_IN, user }));
      dispatch(updateXHR({ xhr: Request.XHR_STATE.SUCCESS }));
    }
  });
};

export const changePassword = async (
  data: PasswordUpdateData,
  dispatch: Dispatch<any>
) => {
  const response = await Request.changePassword(data);
  if (response.data.success) {
    const user = get("user");
    user.passwordResetRequired = false;
    set("user", user);
    dispatch(setUserState({ user, state: USER_STATE.LOGGED_IN }));
  }
  return response.data;
};

export const AddSegment = async (
  dispatch: Dispatch<any>,
  clientUsername: string,
  segmentName: string,
  priceMultiplier?: number,
  description?: string,
  photo?: string | null
): Promise<any> => {
  const staffPermissions = getStaffPermission();
  if (staffPermissions && !staffPermissions.includes("can_generate_barcode")) {
    throw new Error("Access denied!");
  }
  try {
    const response = await Request.addSegment(
      clientUsername,
      segmentName,
      priceMultiplier,
      description,
      photo
    );
    if (response.data.success) {
      const segment: Segment = response.data.segment;
      const user: User = get("user");
      user.segments.push(segment);
      set("user", user);
      dispatch(setUserState({ state: USER_STATE.LOGGED_IN, user }));
      return response;
    } else {
      // If the API call was successful but the action failed on the server,
      // return the error information within the resolved promise.
      return Promise.reject(response);
    }
  } catch (error: any) {
    return Promise.reject(error);
  }
};

export const UpdateSegment = async (
  dispatch: Dispatch<any>,
  segmentId: string,
  updateData: Record<string, any>
) => {
  const staffPermissions = getStaffPermission();
  if (staffPermissions && !staffPermissions.includes("can_generate_barcode")) {
    throw new Error("Access denied!");
  }
  const response = await Request.updateSegment(segmentId, updateData);
  if (response.data.success) {
    const segment: Segment = response.data.updatedSegment;
    let user: User = get("user");
    const userSegments = user.segments.map((s) =>
      s.id === segment.id ? { ...segment } : s
    );
    user = { ...user, segments: userSegments };
    set("user", user);
    Request.getDrinks("Helper", dispatch);
    Request.getFoodProducts("Helper", dispatch);
    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.SUCCESS,
        message: response.data.message,
      })
    );
    dispatch(setUserState({ state: USER_STATE.LOGGED_IN, user }));
  }
};

export const GetMedia = (
  dispatch: Dispatch<any>,
  mediaType: string,
  productType?: ProductType
) => {
  dispatch(updateMediaXhr({ xhr: XHR_STATE.REQUESTING, isFetchMedia: true }));

  let query = "";

  if (productType === ProductType.Drink) {
    query = "?productType=drinks";
  } else if (productType === ProductType.Food) {
    query = "?productType=food";
  }
  Request.getMedia(mediaType, query)
    .then((response) => {
      dispatch(updateMediaXhr({ xhr: XHR_STATE.NONE, isFetchMedia: true }));
      if (!response.data.success) throw new Error("Could not fetch media");
      dispatch(setMediaPhotos({ media: response.data.media }));
      setTimeout(() => {
        dispatch(updateMediaXhr({ xhr: XHR_STATE.NONE, isFetchMedia: false }));
      }, 2000);
      return true;
    })
    .then(() => {})
    .catch((error) => {
      console.log(error);
    });
};

//////////////////////////////////// PACKAGE

export const getPackagesData = async (dispatch: Dispatch<any>) => {
  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));
  try {
    const packages: Package[] = await Request.getSpinPackages();

    dispatch(setPackages(packages));
    dispatch(updateXHR({ xhr: XHR_STATE.SUCCESS }));
    // console.log(packages);
  } catch (error) {
    console.error("Failed to fetch packages:", error);
    dispatch(updateXHR({ xhr: XHR_STATE.FAILED }));
    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.ERROR,
        message: "Could not retrieve packages.",
      })
    );
  }
};

export const createPackageData = async (
  pkg: NewPackage,
  dispatch: Dispatch<any>
) => {
  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));
  try {
    const newPackage = await Request.createSpinPackage(pkg);
    dispatch(addPackage(newPackage));
    dispatch(updateXHR({ xhr: XHR_STATE.SUCCESS }));
    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.SUCCESS,
        message: "Package created successfully!",
      })
    );
    return newPackage;
  } catch (error) {
    console.error("Failed to create package:", error);
    dispatch(updateXHR({ xhr: XHR_STATE.FAILED }));
    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.ERROR,
        message: "Failed to create package. Please try again.",
      })
    );
    return null;
  }
};

export const updatePackageData = async (
  packageId: string,
  pkg: UpdatePackagePayload,
  dispatch: Dispatch<any>
) => {
  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));
  try {
    const updatedPackage = await Request.updateSpinPackage(packageId, pkg);
    dispatch(updatePackage(updatedPackage));
    dispatch(updateXHR({ xhr: XHR_STATE.SUCCESS }));
    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.SUCCESS,
        message: "Package updated successfully!",
      })
    );
    return updatedPackage;
  } catch (error) {
    console.error("Failed to update package details:", error);
    dispatch(updateXHR({ xhr: XHR_STATE.FAILED }));
    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.ERROR,
        message: "Failed to update package. Please try again.",
      })
    );
    return null;
  }
};

export const updateActivePackageData = async (
  packageId: string,
  updateData: { isActive: boolean },
  dispatch: Dispatch<any>
) => {
  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));
  try {
    const updatedTogglePackage = await Request.updateActivePackage(
      packageId,
      updateData
    );
    dispatch(updateActivePackage(updatedTogglePackage));
    dispatch(updateXHR({ xhr: XHR_STATE.SUCCESS }));
    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.SUCCESS,
        message: "Package status updated!",
      })
    );
    return true;
  } catch (error) {
    console.error("Failed to update package", error);
    dispatch(updateXHR({ xhr: XHR_STATE.FAILED }));
    return false;
  }
};

export const deletePackageData = async (
  packageId: string,
  dispatch: Dispatch<any>
) => {
  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));
  try {
    await Request.deleteSpinPackage(packageId);
    dispatch(removePackage(packageId));
    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.SUCCESS,
        message: "Package deleted successfully.",
        timeout: 5000,
      })
    );
    dispatch(updateXHR({ xhr: XHR_STATE.SUCCESS }));
    return true;
  } catch (error) {
    console.error("Failed to delete package", error);
    dispatch(updateXHR({ xhr: XHR_STATE.FAILED }));
    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.ERROR,
        message: "Failed to delete package. Please try again.",
        timeout: 5000,
      })
    );
    return false;
  }
};

/////////////////SETTINGS
export const getCampaignSettings = async (dispatch: Dispatch<any>) => {
  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));
  try {
    const settings: CampaignSettings = await Request.getCampaignSettings();
    dispatch(setSettings(settings));
    dispatch(updateXHR({ xhr: XHR_STATE.SUCCESS }));
  } catch (error) {
    console.error("Failed to fetch settings:", error);
    dispatch(updateXHR({ xhr: XHR_STATE.FAILED }));
    dispatch(
      updateFeedback({
        show: true,
        type: "ERROR",
        message: "Failed to load campaign settings.",
      })
    );
  }
};

export const updateCampaignMaxWins = async (
  maxDailyWins: number,
  dispatch: Dispatch<any>
) => {
  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));
  try {
    const updatedSettings = await Request.updateCampaignSettings(maxDailyWins);
    dispatch(updateMaxDailyWins(updatedSettings.maxDailyWins));
    dispatch(updateXHR({ xhr: XHR_STATE.SUCCESS }));
    dispatch(
      updateFeedback({
        show: true,
        type: "SUCCESS",
        message: "Daily win limit updated successfully!",
      })
    );
    return updatedSettings;
  } catch (error) {
    console.error("Failed to update max daily wins:", error);
    dispatch(updateXHR({ xhr: XHR_STATE.FAILED }));
    dispatch(
      updateFeedback({
        show: true,
        type: "ERROR",
        message: "Update failed. Please try again.",
      })
    );
    return null;
  }
};

export const getComboData = async (dispatch: Dispatch) => {
  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));
  try {
    const combos = await Request.getCombos();
    dispatch(setCombos(combos));
    dispatch(updateXHR({ xhr: XHR_STATE.SUCCESS }));
  } catch (error) {
    console.error("Failed to fetch combos", error);
    dispatch(updateXHR({ xhr: XHR_STATE.FAILED }));
    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.ERROR,
        message: "Could not fetch combos.",
        timeout: 5000,
      })
    );
  }
};

export const createComboData = async (
  combo: NewComboPayload,
  dispatch: Dispatch<any>
) => {
  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));
  try {
    const created = await Request.createCombo(combo);
    dispatch(addCombo(created));
    dispatch(updateXHR({ xhr: XHR_STATE.SUCCESS }));
    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.SUCCESS,
        message: "Combo created successfully!",
        timeout: 5000,
      })
    );
    return created;
  } catch (error) {
    console.error("Failed to create combo", error);
    dispatch(updateXHR({ xhr: XHR_STATE.FAILED }));
    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.ERROR,
        message: "Failed to create combo. Please try again.",
        timeout: 5000,
      })
    );
    return null;
  }
};

export const updateComboData = async (
  comboId: string,
  comboData: UpdateComboPayload,
  dispatch: Dispatch<any>
) => {
  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));
  try {
    const updated = await Request.updateComboItem(comboId, comboData);
    dispatch(updateCombo(updated));
    dispatch(updateXHR({ xhr: XHR_STATE.SUCCESS }));
    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.SUCCESS,
        message: "Combo updated successfully.",
        timeout: 5000,
      })
    );
    return true;
  } catch (error) {
    console.error("Failed to update combo", error);
    dispatch(updateXHR({ xhr: XHR_STATE.FAILED }));
    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.ERROR,
        message: "Failed to update combo. Please try again.",
        timeout: 5000,
      })
    );
    return false;
  }
};

export const deleteComboData = async (
  comboId: string,
  dispatch: Dispatch<any>
) => {
  dispatch(updateXHR({ xhr: XHR_STATE.REQUESTING }));
  try {
    await Request.deleteCombo(comboId);
    dispatch(removeCombo(comboId));
    dispatch(updateXHR({ xhr: XHR_STATE.SUCCESS }));
    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.SUCCESS,
        message: "Combo deleted successfully.",
        timeout: 5000,
      })
    );
    return true;
  } catch (error) {
    console.error("Failed to delete combo", error);
    dispatch(updateXHR({ xhr: XHR_STATE.FAILED }));
    dispatch(
      updateFeedback({
        show: true,
        type: FEEDBACK_VARIANT.ERROR,
        message: "Failed to delete combo. Please try again.",
        timeout: 5000,
      })
    );
    return false;
  }
};
